import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadDeviceConfigSnapshot, restartDeviceConfigTarget, checkDeviceConfigRestartConnection } from '../SiteControl/siteControlApi';

const scope = (target) => JSON.stringify([Number(target?.deviceId || target?.targetDeviceId),
  target?.identityGeneration, target?.endpointRevision, target?.authMode]);
const blocksRestart = (job) => ['queued', 'running'].includes(job?.status)
  || Boolean(job?.blocksRestart && job.deadlineEpochMs > Date.now());
const newRequestKey = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export default function AdbRemoteReboot({ siteId, deviceId, name, onBusyChange, onBeforeRestart }) {
  const [snapshot, setSnapshot] = useState(null);
  const [job, setJob] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const alive = useRef(true);
  const working = useRef(false);
  const confirmButton = useRef(null);
  const target = snapshot?.targets?.find((entry) => Number(entry.deviceId) === Number(deviceId));
  const fresh = snapshot?.expiresAtEpochMs > Date.now();
  const paused = busy || Boolean(confirmation) || blocksRestart(job);

  useEffect(() => {
    onBusyChange?.(paused);
  }, [onBusyChange, paused]);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; onBusyChange?.(false); };
  }, [onBusyChange]);
  useEffect(() => { if (confirmation) confirmButton.current?.focus(); }, [confirmation]);

  const read = useCallback(async () => {
    const next = await loadDeviceConfigSnapshot(siteId);
    if (Number(next?.siteId) !== Number(siteId)) throw new Error('snapshot_scope_mismatch');
    const selected = next?.targets?.find((entry) => Number(entry.deviceId) === Number(deviceId));
    if (alive.current) {
      setSnapshot(next);
      setJob((previous) => selected?.restartJob?.id === previous?.id
        && previous?.recoveryState === 'connection_verified' ? previous : selected?.restartJob || null);
    }
    return { snapshot: next, target: selected };
  }, [siteId, deviceId]);

  const refresh = useCallback(async () => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    try { await read(); if (alive.current) setMessage(''); }
    catch { if (alive.current) setMessage('Could not refresh reboot readiness. Try Refresh reboot status.'); }
    finally { working.current = false; if (alive.current) setBusy(false); }
  }, [read]);
  useEffect(() => { void refresh(); }, [refresh]);

  const restart = async () => {
    if (!confirmation || working.current) return;
    working.current = true;
    setBusy(true);
    setMessage('');
    try {
      // Re-read readiness before mutation; a confirmation cannot migrate to a
      // different identity/endpoint, and the same key survives a lost response.
      const current = await read();
      if (!alive.current) return;
      if (!current.target?.restartAvailable || current.snapshot.expiresAtEpochMs <= Date.now()
          || scope(current.target) !== scope(confirmation.target)) {
        setMessage('Connection details or readiness changed. Cancel and review the target again.');
        return;
      }
      onBeforeRestart?.();
      const result = await restartDeviceConfigTarget(siteId, deviceId, {
        confirmed: true, requestKey: confirmation.requestKey,
        expected: { identityGeneration: confirmation.target.identityGeneration,
          endpointRevision: confirmation.target.endpointRevision, authMode: confirmation.target.authMode },
      });
      if (!result?.success || Number(result.job?.targetDeviceId) !== Number(deviceId)) throw new Error('restart_not_recorded');
      if (!alive.current) return;
      setJob(result.job);
      setConfirmation(null);
    } catch {
      if (alive.current) setMessage('The reboot response was not received. Check the same request again; it will not create a second reboot.');
    } finally {
      working.current = false;
      if (alive.current) setBusy(false);
    }
  };

  const check = async () => {
    if (working.current || !job) return;
    working.current = true;
    setBusy(true);
    try {
      const result = await checkDeviceConfigRestartConnection(siteId, job.id);
      if (alive.current) {
        if (result.success !== true || result.status !== 'completed') throw new Error('connection_not_verified');
        if (Number(result.job?.targetDeviceId) !== Number(deviceId)) throw new Error('job_scope_mismatch');
        setJob(result.job);
        setMessage('ADB connection verified after the request. This does not verify that a reboot occurred.');
      }
    } catch {
      if (alive.current) setMessage('ADB connection could not be verified yet. Wait for the device to return, then check again.');
    } finally { working.current = false; if (alive.current) setBusy(false); }
  };

  return <section aria-label={`ADB reboot for ${name}`}>
    <button type="button" disabled={busy || !fresh || !target?.restartAvailable || blocksRestart(job) || Boolean(confirmation)}
      onClick={() => { setMessage(''); setConfirmation({ target, requestKey: newRequestKey() }); }}>Reboot via ADB</button>
    <button type="button" disabled={busy || Boolean(confirmation)} onClick={refresh}>Refresh reboot status</button>
    {!busy && (!fresh || !target?.restartAvailable) && <p>Reboot is unavailable with the current target or relay setup.</p>}
    {confirmation && <div role="group" aria-label={`Confirm reboot of ${name}`}>
      <p>Reboot {name}? Playback and ADB will disconnect. Debugging may need to be enabled again afterward.</p>
      <button ref={confirmButton} type="button" disabled={busy} onClick={restart}>Confirm reboot</button>
      <button type="button" disabled={busy} onClick={() => { setConfirmation(null); setMessage(''); }}>Cancel reboot</button>
    </div>}
    {job && <div role="status">
      <p>{job.outcome === 'request_sent' ? 'Reboot request sent; waiting for the device to return.'
        : job.outcome === 'delivery_uncertain' ? 'Reboot delivery is uncertain. It will not be replayed automatically.'
          : job.outcome === 'not_sent' ? 'Reboot was not sent.' : `Reboot request: ${job.status}.`}</p>
      {job.recoveryState === 'connection_verified' && <p>Connection verified after the request; reboot itself unverified.</p>}
      {['request_sent', 'delivery_uncertain'].includes(job.outcome) && <button type="button" disabled={busy}
        onClick={check}>Check ADB connection</button>}
    </div>}
    {message && <p role="status">{message}</p>}
  </section>;
}
